/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';

interface SectionHeaderProps {
    title?: string;
    /** Short text shown next to the title (below it on mobile). */
    subtitle?: string;
    /** "Ver más" style link on the right; shown only when both text and URL are set. */
    linkText?: string;
    linkUrl?: string;
    /** Hide individual parts below `md`; when every rendered part is hidden, the header is too. */
    hideOnMobile?: { title?: boolean; subtitle?: boolean; link?: boolean };
    className?: string;
}

/**
 * PriceShoes section header (DS 3.0): bold title with an inline subtitle and an accent
 * "Ver más >" link aligned right. Shared by home sections such as Afíliate and Campañas.
 * Renders nothing when there is neither a title nor a link.
 */
export function SectionHeader({
    title,
    subtitle,
    linkText,
    linkUrl,
    hideOnMobile,
    className,
}: SectionHeaderProps): ReactElement | null {
    const heading = title?.trim();
    const description = subtitle?.trim();
    const showLink = Boolean(linkText?.trim() && linkUrl);

    if (!heading && !showLink) return null;

    const hideTitle = Boolean(heading && hideOnMobile?.title);
    const hideSubtitle = Boolean(description && hideOnMobile?.subtitle);
    const hideLink = Boolean(showLink && hideOnMobile?.link);
    // A part counts as hidden on mobile when it is not rendered at all or is flagged as hidden.
    const textHiddenOnMobile = (!heading || hideTitle) && (!description || hideSubtitle);
    const allHiddenOnMobile = textHiddenOnMobile && (!showLink || hideLink);

    return (
        <div
            data-slot="section-header"
            className={cn(
                'flex items-center justify-between gap-4',
                // Drops the header (and its bottom margin) on mobile instead of leaving an empty gap.
                allHiddenOnMobile && 'max-md:hidden',
                className
            )}>
            <div
                className={cn(
                    'flex min-w-0 flex-col md:flex-row md:items-end md:gap-4',
                    textHiddenOnMobile && 'max-md:hidden'
                )}>
                {heading && (
                    <h2 className={cn('text-2xl font-bold leading-9 text-foreground', hideTitle && 'max-md:hidden')}>
                        {heading}
                    </h2>
                )}
                {description && (
                    <p className={cn('text-base leading-[30px] text-ps-gray-500', hideSubtitle && 'max-md:hidden')}>
                        {description}
                    </p>
                )}
            </div>
            {showLink && (
                <Link
                    to={linkUrl as string}
                    className={cn(
                        'ml-auto inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-bold leading-[22px] text-ps-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ps-focus',
                        hideLink && 'max-md:hidden'
                    )}>
                    {linkText}
                    <ChevronRight className="size-5" aria-hidden="true" />
                </Link>
            )}
        </div>
    );
}
